
import React, { useState } from "react";
import { TableCell, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { PriorityBadge } from "@/components/notifications/StatusBadges";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useNotificationReadStatus } from "@/hooks/useNotificationReadStatus";
import NotificationStatusCell from "./list/NotificationStatusCell";
import NotificationActions from "./list/NotificationActions";
import { SupabaseNotification } from "@/types/notifications";
import { StockStatusBadge } from "./StockStatusBadge";
import { StockAlertDialog } from "./StockAlertDialog";
import { isAwaitingStockStatus } from "@/utils/statusMappings";
import ChatStatusIndicator from "./ChatStatusIndicator";

interface NotificationListItemProps {
  notification: SupabaseNotification;
  formatDate: (date: string) => string;
  onNotificationClick: (id: string) => void;
  onArchive: (e: React.MouseEvent, id: string) => void;
  onRestore: (e: React.MouseEvent, id: string) => void;
  onDelete: (e: React.MouseEvent, id: string) => void;
  onSendWebhook?: (e: React.MouseEvent, id: string) => void;
  isArchived: boolean;
  showBulkSelect?: boolean;
  isSelected?: boolean;
  onToggleSelect?: (id: string) => void;
  unreadChatCount?: number;
}

const NotificationListItem = React.memo(({
  notification,
  formatDate,
  onNotificationClick,
  onArchive,
  onRestore,
  onDelete,
  onSendWebhook,
  isArchived,
  showBulkSelect = false,
  isSelected = false,
  onToggleSelect,
  unreadChatCount = 0
}: NotificationListItemProps) => {
  const { markAsRead, markAsUnread, isNotificationRead } = useNotificationReadStatus();
  const isRead = isNotificationRead(notification.id);
  const isHighPriority = notification.priority === 'hoog';
  const [showStockDialog, setShowStockDialog] = useState(false);
  const voNumbers = notification.vo_number?.split(",") || [];
  const isBulk = voNumbers.length > 1;
  
  const handleMarkAsRead = (e: React.MouseEvent) => {
    e.stopPropagation();
    markAsRead.mutate(notification.id);
  };

  const handleMarkAsUnread = (e: React.MouseEvent) => {
    e.stopPropagation();
    markAsUnread.mutate(notification.id);
  };

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  
  return (
    <>
      <TableRow 
        key={notification.id} 
        className={cn(
          "cursor-pointer hover:bg-slate-50",
        !isRead && "bg-blue-50 font-medium",
          isHighPriority && "border-l-4 border-l-red-500",
          isSelected && "bg-blue-100 hover:bg-blue-100",
          isBulk && "bg-amber-50/50 hover:bg-amber-50"
        )}
        onClick={() => onNotificationClick(notification.id)}
      >
        {showBulkSelect && (
          <TableCell onClick={handleCheckboxClick}>
            <Checkbox
              checked={isSelected}
              onCheckedChange={() => onToggleSelect?.(notification.id)}
              aria-label={`Selecteer melding ${notification.id}`}
            />
          </TableCell>
        )}
        <TableCell>
          <div className="relative flex items-center gap-2 min-w-[100px] h-6">
            {!isBulk && isAwaitingStockStatus(notification.status) ? (
              <StockStatusBadge 
                notificationId={notification.id}
                onClick={() => setShowStockDialog(true)}
              />
            ) : (
              <div className="w-4 h-4 opacity-0" />
            )}
            <ChatStatusIndicator unreadCount={unreadChatCount} />
          </div>
        </TableCell>
      <TableCell className="font-medium capitalize">{notification.type}</TableCell>
      <TableCell>{isBulk ? <Badge className="text-xs bg-amber-100 text-amber-800 border-amber-300">Bulk ({voNumbers.length}x)</Badge> : (notification.vo_number || "-")}</TableCell>
      <TableCell className="max-w-xs truncate">{isBulk ? <span className="text-amber-700 font-medium">Bulk melding</span> : notification.message}</TableCell>
      <NotificationStatusCell notification={notification} />
      <TableCell><PriorityBadge priority={notification.priority} /></TableCell>
      <TableCell>{formatDate(notification.created_at)}</TableCell>
      <NotificationActions
        notificationId={notification.id}
        isRead={isRead}
        isArchived={isArchived}
        onMarkAsRead={handleMarkAsRead}
        onMarkAsUnread={handleMarkAsUnread}
        onView={onNotificationClick}
        onArchive={onArchive}
        onRestore={onRestore}
        onDelete={onDelete}
        onSendWebhook={onSendWebhook}
      />
    </TableRow>
    
    <StockAlertDialog
      notificationId={notification.id}
      voNumber={notification.vo_number}
      open={showStockDialog}
      onOpenChange={setShowStockDialog}
    />
    </>
  );
}, (prevProps, nextProps) => {
  if (prevProps.notification.id !== nextProps.notification.id) return false;
  if (prevProps.isSelected !== nextProps.isSelected) return false;
  if (prevProps.showBulkSelect !== nextProps.showBulkSelect) return false;
  if (prevProps.unreadChatCount !== nextProps.unreadChatCount) return false;
  if (prevProps.notification.updated_at !== nextProps.notification.updated_at) return false;
  return true;
});

NotificationListItem.displayName = 'NotificationListItem';

export default NotificationListItem;
